import type {
  SupportTicketStatus,
  SupportTicketType,
} from '@travel-planner/shared/schemas';
import {
  supportSortFields,
  supportTicketStatusValues,
  supportTicketTypeValues,
} from '@travel-planner/shared/schemas';

// Sort fields the server accepts for `support.list` (findTicketsInput).
export type TicketSortField = (typeof supportSortFields)[number];

// URL search params ↔ typed filters for the support management page.
// `status` is MULTI-value (the server input has always taken an array): a
// triager narrows to "open + in progress" in one pass rather than paging the
// list twice. `type` stays single-value until there is a reason to widen it.
// Mirrors apps/incidents/web/src/features/incidents-filters/model/search-params.ts.
export type TicketsSearch = {
  status?: SupportTicketStatus[];
  type?: SupportTicketType;
  search?: string;
  // Narrows a cross-organization listing to one organization. Only the `system`
  // role ever sees the control that writes it; for anyone else the server pins
  // the scope to their own org and ignores this value.
  organizationId?: string;
  page?: number;
  sortBy?: TicketSortField;
  sortOrder?: 'asc' | 'desc';
};

const asOne = <T extends string>(
  values: readonly T[],
  value: unknown
): T | undefined =>
  typeof value === 'string' && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;

// Multi-value filter. A bare `?status=open` (one occurrence, so a plain string
// rather than an array) is accepted and folded into a one-element list, so
// links minted before the filter went multi-select keep working. Unknown
// members are dropped, and an all-dropped list becomes `undefined` — "no
// filter" and "a filter matching nothing" must not look the same in the URL.
const asMany = <T extends string>(
  values: readonly T[],
  value: unknown
): T[] | undefined => {
  const raw = Array.isArray(value) ? value : [value];
  const filtered = raw.filter((v): v is T =>
    typeof v === 'string' ? (values as readonly string[]).includes(v) : false
  );
  return filtered.length > 0 ? filtered : undefined;
};

export const validateTicketsSearch = (
  search: Record<string, unknown>
): TicketsSearch => {
  const out: TicketsSearch = {};
  const status = asMany(supportTicketStatusValues, search.status);
  if (status) out.status = status;
  const type = asOne(supportTicketTypeValues, search.type);
  if (type) out.type = type;
  if (typeof search.search === 'string' && search.search.trim().length > 0) {
    out.search = search.search;
  }
  // Kept as a plain non-empty string rather than uuid-validated here: the
  // server's `findTicketsInput` is the authority on shape, and a bad value can
  // only ever yield an empty list (never a widened scope).
  if (
    typeof search.organizationId === 'string' &&
    search.organizationId.trim().length > 0
  ) {
    out.organizationId = search.organizationId;
  }
  const page = Number(search.page);
  if (Number.isFinite(page) && page > 1) out.page = Math.floor(page);
  const sortBy = asOne(supportSortFields, search.sortBy);
  if (sortBy) out.sortBy = sortBy;
  if (search.sortOrder === 'asc' || search.sortOrder === 'desc') {
    out.sortOrder = search.sortOrder;
  }
  return out;
};
